import { useCallback, useEffect, useState } from 'react';

export type AsyncState<T> =
  | { status: 'loading' }
  | { status: 'error'; error: Error }
  | { status: 'done'; data: T };

/**
 * Runs an async load when the inputs change and keeps its state: loading, error or done.
 * A late answer for old inputs is dropped. reload() runs it again (for the retry button).
 */
export function useAsync<T>(load: () => Promise<T>, inputs: readonly unknown[]): AsyncState<T> & { reload: () => void } {
  const [state, setState] = useState<AsyncState<T>>({ status: 'loading' });
  const [round, setRound] = useState(0);
  useEffect(() => {
    let live = true;
    setState({ status: 'loading' });
    load().then(
      (data) => live && setState({ status: 'done', data }),
      (error: unknown) => live && setState({ status: 'error', error: error instanceof Error ? error : new Error(String(error)) }),
    );
    return () => {
      live = false;
    };
    // The caller lists the inputs, like a dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...inputs, round]);
  const reload = useCallback(() => setRound((r) => r + 1), []);
  return { ...state, reload };
}
